import { Children, Component, createRef } from 'react';

import { Commons } from '../../../commons/commons';
import { GenericMouseEvent } from '../../../types/types';
import { UIUtils } from '../../../utils/dom-utils';
import { WindowUtils } from '../../../utils/window.utils';
import SortableItemComponent from './sortable-item.component';
import * as styles from './sortable.component.module.scss'
import { OptionalChildrenProps } from '../../../types/react.types';

interface SortableComponentProps extends OptionalChildrenProps {
    items: { uuid: string, [key: string]: any }[];
    /**
     * class which will be the active area consider as the handler. Usually use 'sort-handler'
     * To ignore an element with in an parent element from been a handler use prefix not-<sortHandlerClass>.
     * 
     * If className `sort-handler` used then cursor: pointer is added to the active areas at the global level
     */
    sortHandlerClass: string;
    classes?: string[]
    orderChanged: (newOrder) => void;
    sortEnded: (newOrder) => void;
}

interface Position {
    x: number;
    y: number;
}

export default class SortableComponent extends Component<SortableComponentProps> {
    sortableRef = createRef<HTMLDivElement>();
    debug = Commons.DEBUG;
    activeElement: HTMLDivElement;
    ghostElement: HTMLDivElement;
    sortableElementList: HTMLElement[];
    startPosition: Position;
    startOffsetPosition: Position;

    getElementMargin(element: HTMLDivElement) {
        return {
            // x: element.get
        };
    }

    addPostions(p1: Position, p2: Position) {
        return { x: p1.x + p2.x, y: p1.y + p2.y };
    }

    subtractPostions(p1: Position, p2: Position) {
        return { x: p1.x - p2.x, y: p1.y - p2.y };
    }

    getSortableElementList(): HTMLElement[] {
        if (!this.sortableRef || !this.sortableRef.current) {
            return [];
        }
        return Array.from<HTMLElement>(
            this.sortableRef.current.getElementsByClassName("sortable-item") as any
        );
    }

    storeSortableElementList() {
        this.sortableElementList = this.getSortableElementList();
    }

    insertBefore(newChild: HTMLElement, refElement: HTMLElement) {
        refElement.parentNode.insertBefore(newChild, refElement);
    }

    /**
     * 1  => src after target
     * 0  => same
     * -1 => src before target
     */
    compareSibling(sourceElement: HTMLElement, targetElement: HTMLElement) {
        const result = sourceElement.compareDocumentPosition(targetElement);
        if (this.debug) console.log('result: ' + result + " - " + Node.DOCUMENT_POSITION_FOLLOWING);
        return result === 0 ? 0 : (result === Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
    }

    insertAt(newChild: HTMLElement, refElement: HTMLElement) {
        if (this.compareSibling(newChild, refElement) === 1) {
            this.insertBefore(newChild, refElement);
        } else if (this.compareSibling(newChild, refElement) === -1) {
            if (refElement.nextSibling) {
                this.insertBefore(newChild, refElement.nextSibling as HTMLElement);
            } else {
                refElement.parentElement.removeChild(newChild);
                refElement.parentElement.appendChild(newChild);
            }
        }
    }

    createGhostElement(srcElement: HTMLElement, position: Position) {
        const rect = srcElement.getBoundingClientRect()
        const ghostElement = srcElement.cloneNode(true) as HTMLDivElement;
        ghostElement.classList.add('ghost-element');
        ghostElement.style.position = "absolute";
        ghostElement.style.left = `${position.x}px`;
        ghostElement.style.top = `${position.y}px`;
        ghostElement.style.marginTop = "0px";
        ghostElement.style.marginLeft = "0px";
        ghostElement.style.width = `${rect.width}px`;
        ghostElement.style.height =  `${rect.height}px`;
        ghostElement.style.backgroundColor = "rgb(255 103 155)";
        ghostElement.style.userSelect = "none";
        ghostElement.style.pointerEvents = "none";
        ghostElement.style.opacity = '0.5';
        ghostElement.style.zIndex = '1000';
        this.ghostElement = ghostElement;
        document.body.appendChild(this.ghostElement);
    }

    removeGhostElement() {
        this.ghostElement.remove();
    }

    moveGhostElement(position: Position) {
        this.ghostElement.style.left = `${position.x}px`;
        this.ghostElement.style.top = `${position.y}px`;
    }

    handleMouseUp(e) {
        if (this.debug) console.log("mouse up");
        this.removeGhostElement();


        const newItemList = this.sortInputItemsToMatchCurrentSortOrder();
        this.props.sortEnded(newItemList);
        this.storeSortableElementList();
    }

    handleMouseDown(event) {
        event.preventDefault();
        this.storeSortableElementList();
        const e = UIUtils.toGenericMouseEvent(event);
        if (this.debug) console.log("mouse down");
        if (this.debug) console.log(e.target);

        const target = e.target as HTMLElement;
        if (this.props.sortHandlerClass) {
            const closestSortableElement = target.closest<HTMLDivElement>(
                `.${this.props.sortHandlerClass}`
            );
            if (!closestSortableElement) {
                return;
            }

            const closestNotSortableElement = target.closest<HTMLDivElement>(
                `.not-${this.props.sortHandlerClass}`
            );
            if (closestNotSortableElement) {
                return;
            }

        }
        const closestSortableElement = target.closest<HTMLDivElement>(
            ".sortable-item"
        );
        this.activeElement = closestSortableElement;
        this.startPosition = { x: e.pageX, y: e.pageY };
        const elRect = closestSortableElement.getBoundingClientRect();
        // Change this from getElementScrollPosition
        const scroll = UIUtils.getElementWindowPosition(this.activeElement);
        this.startOffsetPosition = {
            //x: e.pageX - (elRect.left), // scroll.x + 
            //y: e.pageY - (elRect.top), // Note: Change (scroll.y + elRect.top) to elRect.top to fix issue when container parent is scrolled
            x: e.pageX - (scroll.x + elRect.left),
            y: e.pageY - (scroll.y + elRect.top),
        };


        const mouseMoveFn = ev => this.handleMouseMove(ev);
        const mouseUpFn = ev => this.handleMouseUp(ev);
        WindowUtils.addMoveMoveHandlerToWindow(mouseMoveFn, true);
        WindowUtils.addMoveUpHandlerToWindow(mouseUpFn);

        this.createGhostElement(
            this.activeElement,
            this.subtractPostions(this.startPosition, this.startOffsetPosition)
        );
    }

    closestElementToGhostElement(event: GenericMouseEvent) {
        const element = document.elementFromPoint(event.clientX, event.clientY);
        if (element) {
            const closest = element.closest<HTMLElement>(
                ".sortable-item"
            );
            return closest;
        }
        return null;
    }

    handleMouseMove(e: GenericMouseEvent) {
        const closest = this.closestElementToGhostElement(e);
        if (closest && this.activeElement !== closest) {
            if (this.debug) console.log("Some other");
            this.insertAt(this.activeElement, closest);
            this.updateOrderAndStoreSortableElementList();
        }
        if (this.debug) console.log("mouse move", closest?.className);
        const movePosition = { x: e.pageX, y: e.pageY };
        this.moveGhostElement(
            // movePosition
            this.subtractPostions(movePosition, this.startOffsetPosition)
        );
    }

    updateOrderAndStoreSortableElementList() {
        // const newItemList = this.sortInputItemsToMatchCurrentSortOrder();
        // this.props.orderChanged(newItemList);
        // this.storeSortableElementList();
    }

    sortInputItemsToMatchCurrentSortOrder() {
        const currentOrder = this.getSortableElementList();
        const newItemList = [];
        currentOrder.forEach((item) => {
            const originalIndex = this.sortableElementList.indexOf(item);
            if (this.debug) console.log(`Index: ${originalIndex}`);
            newItemList.push(this.props.items[originalIndex]);
        });
        return newItemList;
    }

    render() {
        return (
            <div id="sortable" ref={this.sortableRef} className={`${(this.props.classes||[]).join(" ")}`}>
                {
                    Children.toArray(this.props.children).filter(c => c['props']['data-notsortable']).map((child, index) => {
                        return <div key={`non-sortable-${index}`} className={styles['non-sortable-item']} style={{ display: 'inline-block' }}>{child}</div>;
                    })
                }
                {
                    Children.toArray(this.props.children).filter(c => !c['props']['data-notsortable']).map((child, index) => {
                        return <SortableItemComponent
                            key={`sortable-item-${index}`}
                            index={index}
                            uniquekey={this.props.items[index]?.uuid}
                            onTouchStart={e => this.handleMouseDown(e)}
                            onMouseDown={(e) => this.handleMouseDown(e)}>{child}</SortableItemComponent>;
                    })
                }
            </div>
        );
    }
}
